import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { injectIntl } from 'react-intl';
import { Typography, Divider, Button, Hidden } from '@material-ui/core';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { keyEmptyCheck } from 'aurora-utils/utils';
import {
  fetchGovernmentCompanies,
  fetchUserDetails,
  fetchCompanyDetails,
} from 'aurora-composition/modules/common/actions';
import { COMMON_REDUCER_CLEAR_ERROR } from 'aurora-composition/modules/common/constants';
import { ADGMResponsiveModal, ADGMAlert, Loading } from 'aurora-components';
import Filters from './Filters';
import UAEResidentBuyer from './UAEResidentBuyer';
import CompanyBuyer from './CompanyBuyer';
import GovernmentBuyer from './GovernmentBuyer';
import useBuyerDetailsActions from '../helpers';
import { BUYER_TYPES, FILTERS_KEYS } from '../constants';

import styles from './styles';

const GOVT_COMPANIES_LIST_PAGE_SIZE = 5;

function AddBuyerModal({
  classes,
  intl,
  locale,
  openModal,
  isEditFlow,
  buyer,
  error,
  closeModalHandler,
  addBuyerHandler,
}) {
  const { loading: commonLoading, error: commonError } = useSelector(state => state.commonReducer);
  const dispatch = useDispatch();

  const { transformDetails, getBuyerType, getFiltersInitialState } = useBuyerDetailsActions();
  const [buyerType, setBuyerType] = useState(getBuyerType(buyer) ?? BUYER_TYPES.UAE_RESIDENT);
  const [buyerDetails, setBuyerDetails] = useState(transformDetails(buyer, getBuyerType(buyer)) ?? {});
  const [fetchBuyerDetailsError, setFetchBuyerDetailsError] = useState(null);
  const [curGovtCompaniesListPage, setCurGovtCompaniesListPage] = useState(1);
  const [govtCompaniesData, setGovtCompaniesData] = useState(null);
  const [isCompaniesDataFetched, setIsCompaniesDataFetched] = useState(false);

  const [filters, setFilters] = useState(getFiltersInitialState(FILTERS_KEYS[buyerType]) ?? {});

  const handleFilterChange = (key, value) => {
    setFilters(curState => ({ ...curState, [key]: value }));
  };

  const clearErrorIfExists = useCallback(() => {
    if (!isEmpty(commonError)) {
      dispatch({
        type: COMMON_REDUCER_CLEAR_ERROR,
      });
    }
  }, [commonError]);

  const clearCompaniesResponse = () => {
    setGovtCompaniesData(null);
    setIsCompaniesDataFetched(false);
    setCurGovtCompaniesListPage(1);
  };

  const handleCloseModal = () => {
    clearErrorIfExists();
    closeModalHandler();
  };

  const handleBuyerTypeChange = value => {
    if (buyerType === value) return;
    setBuyerType(value);
    setFilters(getFiltersInitialState(FILTERS_KEYS[value]));
    setBuyerDetails({});
  };

  const handleChangeBuyerDetails = (key, value) => {
    setBuyerDetails(curData => ({ ...curData, [key]: value }));
  };

  const handleFetchBuyerDetails = type => {
    setBuyerDetails({});
    setFetchBuyerDetailsError(null);
    clearErrorIfExists();
    switch (type) {
      case BUYER_TYPES.UAE_RESIDENT:
        const eid = filters?.[FILTERS_KEYS?.[type]?.eid]?.replace(/-/g, '');
        const params = filters?.[FILTERS_KEYS?.[type]?.dateOfBirth]
          ? { dob: filters?.[FILTERS_KEYS?.[type]?.dateOfBirth] }
          : null;

        dispatch(fetchUserDetails(eid, params)).then(res => {
          if (res?.success) {
            setBuyerDetails(transformDetails(res?.result, BUYER_TYPES.UAE_RESIDENT));
          } else {
            setFetchBuyerDetailsError(res);
          }
        });
        break;
      case BUYER_TYPES.COMPANY:
        dispatch(fetchCompanyDetails(filters[FILTERS_KEYS?.[type]?.tradeLicense])).then(res => {
          if (res?.success) {
            setBuyerDetails(transformDetails(res?.result, BUYER_TYPES.COMPANY));
          } else {
            setFetchBuyerDetailsError(res);
          }
        });
        break;
      case BUYER_TYPES.GOVERNMENT:
        const licenseNumber = filters[FILTERS_KEYS?.[type]?.licenseNumber];
        const nameEn = filters[FILTERS_KEYS?.[type]?.nameEn];
        const nameAr = filters[FILTERS_KEYS?.[type]?.nameAr];
        clearCompaniesResponse();
        dispatch(
          fetchGovernmentCompanies({
            pageNumber: curGovtCompaniesListPage - 1,
            pageSize: GOVT_COMPANIES_LIST_PAGE_SIZE,
            ...(licenseNumber && { licenseNumber }),
            ...(nameEn && { nameEn }),
            ...(nameAr && { nameAr }),
          }),
        ).then(res => {
          if (res?.success) {
            setGovtCompaniesData(res?.result);
          }
          if (!isCompaniesDataFetched) setIsCompaniesDataFetched(true);
        });
        break;

      default:
        break;
    }
  };

  const handleGovtPageChange = page => {
    setCurGovtCompaniesListPage(page);
    handleFetchBuyerDetails(buyerType);
  };

  const handleAddBuyer = () => {
    handleCloseModal();
    addBuyerHandler({
      ...(buyerType === BUYER_TYPES.UAE_RESIDENT && {
        userId: buyerDetails?.userId,
        email: buyerDetails?.email,
        phoneNo: buyerDetails?.phoneNumber,
      }),
      ...(buyerType === BUYER_TYPES.COMPANY && {
        companyId: buyerDetails?.companyId,
        email: buyerDetails?.email,
        phoneNo: buyerDetails?.phoneNumber,
      }),
      ...(buyerType === BUYER_TYPES.GOVERNMENT && {
        companyId: buyerDetails?.companyId,
        email: buyerDetails?.companyEmail,
        phoneNo: buyerDetails?.mobileNumber,
      }),
    });
  };

  const handleOnGovernmentCompanySelect = companyData => {
    setBuyerDetails(companyData);
  };

  return (
    <ADGMResponsiveModal
      open={openModal}
      toggleModal={handleCloseModal}
      title={keyEmptyCheck(isEditFlow ? 'edit_party_details' : 'add_party')}
      disablePortal
      disableScrollLock={false}
      customClasses={{
        modal: classes.addBuyerModal,
        header: classes.addBuyerModalHeader,
      }}
    >
      {commonLoading && <Loading />}
      <div className={classes.modalContent}>
        {!isEmpty(error) && (
          <ADGMAlert type='error' customClasses={{ container: classes.errorAlertContainer }}>
            <Typography className={classes.errorAlertMessage}>
              {keyEmptyCheck(
                error?.message || error?.statusCode ? `error_${fetchBuyerDetailsError?.statusCode}` : error,
              )}
            </Typography>
          </ADGMAlert>
        )}
        {!isEmpty(commonError) && (
          <ADGMAlert type='error' customClasses={{ container: classes.errorAlertContainer }}>
            <Typography className={classes.errorAlertMessage}>
              {keyEmptyCheck(
                commonError?.message || commonError?.statusCode
                  ? `error_${fetchBuyerDetailsError?.statusCode}`
                  : commonError,
              )}
              {keyEmptyCheck(`error_${fetchBuyerDetailsError?.statusCode}`)}
            </Typography>
          </ADGMAlert>
        )}
        {!isEmpty(fetchBuyerDetailsError) && (
          <ADGMAlert type='error' customClasses={{ container: classes.errorAlertContainer }}>
            <Typography className={classes.errorAlertMessage}>
              {keyEmptyCheck(`error_${fetchBuyerDetailsError?.statusCode}`)}
            </Typography>
          </ADGMAlert>
        )}
        {isEditFlow !== true && (
          <Filters
            intl={intl}
            buyerType={buyerType}
            filters={filters}
            onBuyerTypeChangeHandler={handleBuyerTypeChange}
            onFilterChangeHandler={handleFilterChange}
            onFetchBuyerDetailsHandler={handleFetchBuyerDetails}
          />
        )}
        {buyerType === BUYER_TYPES.UAE_RESIDENT && !isEmpty(buyerDetails) && (
          <UAEResidentBuyer
            intl={intl}
            locale={locale}
            isEditFlow={isEditFlow}
            buyer={buyerDetails}
            onChangeHandler={handleChangeBuyerDetails}
          />
        )}
        {buyerType === BUYER_TYPES.COMPANY && !isEmpty(buyerDetails) && (
          <CompanyBuyer
            intl={intl}
            locale={locale}
            isEditFlow={isEditFlow}
            buyer={buyerDetails}
            onChangeHandler={handleChangeBuyerDetails}
          />
        )}
        {buyerType === BUYER_TYPES.GOVERNMENT && (!isEmpty(govtCompaniesData) || isCompaniesDataFetched) && (
          <GovernmentBuyer
            locale={locale}
            currentPage={curGovtCompaniesListPage}
            companiesResponse={govtCompaniesData}
            selectedCompanyId={buyerDetails?.companyId}
            filters={filters}
            showNoResultScreen={isCompaniesDataFetched}
            onCompanySelectHandler={handleOnGovernmentCompanySelect}
            onPageChangeHandler={handleGovtPageChange}
            clearCompaniesResponseHandler={clearCompaniesResponse}
          />
        )}

        {((buyerType === BUYER_TYPES.GOVERNMENT && !isEmpty(govtCompaniesData)) || !isEmpty(buyerDetails)) && (
          <>
            <Hidden xsDown>
              <Divider className={classes.divider} />
            </Hidden>
            <div className={classes.footer}>
              <Hidden smDown>
                <Button className={classNames(classes.footerBtn, classes.footerCancelBtn)} onClick={handleCloseModal}>
                  {keyEmptyCheck('cancel')}
                </Button>
              </Hidden>
              <Button
                className={classNames(classes.footerBtn, classes.footerAddBtn, {
                  [classes.disabledBtn]: isEmpty(buyerDetails),
                })}
                onClick={handleAddBuyer}
                disabled={isEmpty(buyerDetails) || fetchBuyerDetailsError}
              >
                {keyEmptyCheck(isEditFlow ? 'save_changes' : 'Add_buyer')}
              </Button>
            </div>
          </>
        )}
      </div>
    </ADGMResponsiveModal>
  );
}

AddBuyerModal.propTypes = {
  classes: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  isEditFlow: PropTypes.bool.isRequired,
  openModal: PropTypes.bool.isRequired,
  buyer: PropTypes.any.isRequired,
  error: PropTypes.any.isRequired,
  closeModalHandler: PropTypes.func.isRequired,
  addBuyerHandler: PropTypes.func.isRequired,
};

export default withStyles(styles)(injectIntl(AddBuyerModal));
